import { useEffect, useState, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { DateInput } from '../../../../shared/components/DateInput.js';

interface MisRow {
  readonly misReportId: string;
  readonly misType: string;
  readonly periodStart: string;
  readonly periodEnd: string;
  readonly status: string;
  readonly generatedAt?: string;
}

interface MisForm {
  misType: string;
  periodStart: string;
  periodEnd: string;
}

const MIS_TYPES = ['DAILY', 'WEEKLY', 'MONTHLY', 'QUARTERLY', 'ANNUAL', 'CAMPAIGN', 'VENDOR', 'FINANCIAL', 'OPERATIONAL'] as const;

/** L16.09 — MIS Center (`/reports/mis`). Generates MIS reports for a period and
 *  lists previously generated reports (M3). */
export function M16MisCenterPage(): ReactNode {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [rows, setRows] = useState<MisRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<MisForm>({ misType: 'MONTHLY', periodStart: '', periodEnd: '' });
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  async function load(): Promise<void> {
    setRows(await apiRequest<MisRow[]>('/v1/m16/analytics/mis', { token }));
  }

  useEffect(() => {
    let alive = true;
    apiRequest<MisRow[]>('/v1/m16/analytics/mis', { token })
      .then((r) => { if (alive) setRows(r); })
      .catch((e) => { if (alive) setError((e as Error).message); });
    return () => { alive = false; };
  }, [token]);

  async function generate(): Promise<void> {
    setBusy(true);
    setActionError(null);
    try {
      await apiRequest('/v1/m16/analytics/mis/generate', {
        method: 'POST',
        token,
        body: { misType: form.misType, periodStart: form.periodStart, periodEnd: form.periodEnd },
      });
      await load();
    } catch (e) {
      setActionError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  if (error) return <p role="alert">{error}</p>;
  if (!rows) return <p>{t('m16.mis.loading')}</p>;
  return (
    <main aria-label="mis-center">
      <h1>{t('m16.mis.title')}</h1>
      <section aria-label="mis-form">
        <select aria-label="mis-type" data-testid="mis-type"
          value={form.misType} onChange={(e) => setForm((f) => ({ ...f, misType: e.target.value }))}>
          {MIS_TYPES.map((x) => <option key={x} value={x}>{x}</option>)}
        </select>
        <DateInput aria-label="mis-period-start" data-testid="mis-period-start" placeholder={t('m16.mis.placeholderPeriodStart')}
          value={form.periodStart} onValueChange={(nextValue) => setForm((f) => ({ ...f, periodStart: nextValue }))} />
        <DateInput aria-label="mis-period-end" data-testid="mis-period-end" placeholder={t('m16.mis.placeholderPeriodEnd')}
          value={form.periodEnd} onValueChange={(nextValue) => setForm((f) => ({ ...f, periodEnd: nextValue }))} />
        <button type="button" data-testid="mis-generate-btn" disabled={busy || !form.periodStart.trim() || !form.periodEnd.trim()} onClick={generate}>{t('m16.mis.generateBtn')}</button>
      </section>
      {actionError && <p role="alert">{actionError}</p>}
      <table aria-label="mis-reports-table">
        <thead>
          <tr><th>{t('m16.mis.colType')}</th><th>{t('m16.mis.colPeriodStart')}</th><th>{t('m16.mis.colPeriodEnd')}</th><th>{t('m16.mis.colStatus')}</th></tr>
        </thead>
        <tbody>
          {rows.map((m) => (
            <tr key={m.misReportId}>
              <td>{m.misType}</td>
              <td>{m.periodStart}</td>
              <td>{m.periodEnd}</td>
              <td>{m.status}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </main>
  );
}
